import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Settings, Save, Check, X } from 'lucide-react';
import * as adminService from '../../../services/adminService';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';

/**
 * Plan Settings Component
 * Admin interface for configuring plan features and visibility
 */
export default function PlanSettings({ planId, onSave }) {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [config, setConfig] = useState({
    enableTraining: true,
    enableNutrition: true,
    enableProgressTracking: true,
    enableMessaging: true,
    enableResources: true,
    enableCheckpoints: true,
    showWorkoutCalendar: true,
    showExerciseLibrary: true,
    showMealPlans: true,
    showMacroTracker: true,
    showRecipeLibrary: true,
    showProgressPhotos: true,
    showMeasurements: true,
    showCheckIns: true,
    accessLevel: 'standard',
  });

  useEffect(() => {
    if (planId && user) {
      loadConfiguration();
    }
  }, [planId, user]);

  const loadConfiguration = async () => {
    setIsLoading(true);
    try {
      // Get plan details to access configuration
      const { data, error } = await supabase
        .from('plan_configurations')
        .select('*')
        .eq('program_id', planId)
        .single();

      if (!error && data) {
        setConfig({
          enableTraining: data.enable_training !== false,
          enableNutrition: data.enable_nutrition !== false,
          enableProgressTracking: data.enable_progress_tracking !== false,
          enableMessaging: data.enable_messaging !== false,
          enableResources: data.enable_resources !== false,
          enableCheckpoints: data.enable_checkpoints !== false,
          showWorkoutCalendar: data.show_workout_calendar !== false,
          showExerciseLibrary: data.show_exercise_library !== false,
          showMealPlans: data.show_meal_plans !== false,
          showMacroTracker: data.show_macro_tracker !== false,
          showRecipeLibrary: data.show_recipe_library !== false,
          showProgressPhotos: data.show_progress_photos !== false,
          showMeasurements: data.show_measurements !== false,
          showCheckIns: data.show_check_ins !== false,
          accessLevel: data.access_level || 'standard',
        });
      }
    } catch (error) {
      console.error('Error loading configuration:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!user) return;
    setIsSaving(true);

    try {
      const result = await adminService.configurePlanFeatures(user.id, planId, config);
      if (result.success) {
        if (onSave) {
          onSave();
        }
      }
    } catch (error) {
      console.error('Error saving configuration:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const ToggleSwitch = ({ label, checked, onChange, description }) => (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
      <div className="flex-1">
        <label className="text-sm font-medium text-gray-900">{label}</label>
        {description && (
          <p className="text-xs text-gray-600 mt-1">{description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
          checked ? 'bg-primary-600' : 'bg-gray-300'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Settings className="w-6 h-6" />
          Plan Configuration
        </h2>
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="px-6 py-2 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 flex items-center gap-2"
        >
          <Save className="w-5 h-5" />
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Feature Toggles */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Feature Toggles</h3>
        <div className="space-y-3">
          <ToggleSwitch
            label="Enable Training Protocol"
            checked={config.enableTraining}
            onChange={(value) => setConfig({ ...config, enableTraining: value })}
            description="Allow clients to view and interact with training workouts"
          />
          <ToggleSwitch
            label="Enable Nutrition Plan"
            checked={config.enableNutrition}
            onChange={(value) => setConfig({ ...config, enableNutrition: value })}
            description="Allow clients to view meal plans and track macros"
          />
          <ToggleSwitch
            label="Enable Progress Tracking"
            checked={config.enableProgressTracking}
            onChange={(value) => setConfig({ ...config, enableProgressTracking: value })}
            description="Allow clients to log progress, photos, and measurements"
          />
          <ToggleSwitch
            label="Enable Messaging"
            checked={config.enableMessaging}
            onChange={(value) => setConfig({ ...config, enableMessaging: value })}
            description="Allow direct messaging between client and coach"
          />
          <ToggleSwitch
            label="Enable Resources"
            checked={config.enableResources}
            onChange={(value) => setConfig({ ...config, enableResources: value })}
            description="Show resources library to clients"
          />
          <ToggleSwitch
            label="Enable Checkpoints"
            checked={config.enableCheckpoints}
            onChange={(value) => setConfig({ ...config, enableCheckpoints: value })}
            description="Show milestones and progress checkpoints"
          />
        </div>
      </div>

      {/* Module Visibility */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Module Visibility</h3>
        <div className="space-y-3">
          <ToggleSwitch
            label="Show Workout Calendar"
            checked={config.showWorkoutCalendar}
            onChange={(value) => setConfig({ ...config, showWorkoutCalendar: value })}
            description="Display workout calendar view"
          />
          <ToggleSwitch
            label="Show Exercise Library"
            checked={config.showExerciseLibrary}
            onChange={(value) => setConfig({ ...config, showExerciseLibrary: value })}
            description="Display exercise library browser"
          />
          <ToggleSwitch
            label="Show Meal Plans"
            checked={config.showMealPlans}
            onChange={(value) => setConfig({ ...config, showMealPlans: value })}
            description="Display meal plan schedule"
          />
          <ToggleSwitch
            label="Show Macro Tracker"
            checked={config.showMacroTracker}
            onChange={(value) => setConfig({ ...config, showMacroTracker: value })}
            description="Display macro tracking charts"
          />
          <ToggleSwitch
            label="Show Recipe Library"
            checked={config.showRecipeLibrary}
            onChange={(value) => setConfig({ ...config, showRecipeLibrary: value })}
            description="Display recipe browser"
          />
          <ToggleSwitch
            label="Show Progress Photos"
            checked={config.showProgressPhotos}
            onChange={(value) => setConfig({ ...config, showProgressPhotos: value })}
            description="Allow photo uploads and gallery"
          />
          <ToggleSwitch
            label="Show Measurements"
            checked={config.showMeasurements}
            onChange={(value) => setConfig({ ...config, showMeasurements: value })}
            description="Allow body measurement tracking"
          />
          <ToggleSwitch
            label="Show Check-Ins"
            checked={config.showCheckIns}
            onChange={(value) => setConfig({ ...config, showCheckIns: value })}
            description="Allow weekly check-in submissions"
          />
        </div>
      </div>

      {/* Access Level */}
      <div className="bg-white rounded-xl shadow-lg p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Access Level</h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Plan Access Tier
          </label>
          <select
            value={config.accessLevel}
            onChange={(e) => setConfig({ ...config, accessLevel: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="standard">Standard</option>
            <option value="premium">Premium</option>
            <option value="vip">VIP</option>
          </select>
          <p className="text-xs text-gray-500 mt-2">
            Access level determines which features are available to the client
          </p>
        </div>
      </div>
    </div>
  );
}
